// services/assignedAssetsService.js
import { repo } from "../dbservice/repo";

class AssignedAssetsService {
  constructor() {
    this.collectionName = "assets";
    this.locationsCollection = "locations"; // Make sure this matches your DB collection name for locations
  }

  /**
   * SSR-safe current user resolver.
   */
  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[AssignedAssetsService] Failed to parse userData", e);
      return null;
    }
  }

  /**
   * Resolve uid/email/companyId from an explicit user OR localStorage fallback.
   */
  _resolveIdentity(currentUser = {}) {
    const stored = this.currentUser || {};
    const user = currentUser?.email || currentUser?.uid || currentUser?.id
      ? currentUser
      : stored;

    const uid =
      user?.uid || user?.id || user?.userId ||
      stored?.uid || stored?.id || stored?.userId || null;

    const email = user?.email || stored?.email || null;
    const companyId = user?.companyId || stored?.companyId || user?.company || null;

    return { uid, email, companyId };
  }

  /**
   * Get user profile via repository.
   */
  async getUserProfile(uid) {
    if (!uid) throw new Error("User ID is required");
    const user = await repo.fetchById("users", uid);
    if (!user) throw new Error("User document not found");
    return user;
  }

  /**
   * Fetch raw assets assigned to a user via repo.
   */
  async fetchRawAssignedAssets({ companyId, uid, email }) {
    if (!companyId || !uid) {
      throw new Error("companyId and uid are required");
    }

    const queries = [
      repo.fetchData(this.collectionName, [
        { field: "companyId", value: companyId },
        { field: "assignedTo", value: uid },
      ]),
      repo.fetchData(this.collectionName, [
        { field: "companyId", value: companyId },
        { field: "assignedToUid", value: uid },
      ]),
    ];

    if (email) {
      queries.push(
        repo.fetchData(this.collectionName, [
          { field: "companyId", value: companyId },
          { field: "assignedToName", value: email },
        ])
      );
    }

    const results = await Promise.all(queries);
    const combined = results.flat();

    return combined.filter(
      (asset, i, self) => i === self.findIndex((a) => a.id === asset.id)
    );
  }

  /**
   * Live subscription for assigned assets.
   */
  subscribeToAssignedAssets({ companyId, uid, email }, callback) {
    if (!companyId || !uid) {
      throw new Error("companyId and uid are required");
    }

    const buckets = { byAssignedTo: [], byAssignedToUid: [], byEmail: [] };

    const emit = () => {
      const combined = [
        ...buckets.byAssignedTo,
        ...buckets.byAssignedToUid,
        ...buckets.byEmail,
      ];
      const unique = combined.filter(
        (a, i, self) => i === self.findIndex((x) => x.id === a.id)
      );
      const formatted = unique.map((raw) =>
        this.formatAsset(raw, { userEmail: email })
      );
      formatted.sort(
        (a, b) =>
          (b.updatedAt || b.assignedOn || b.createdAt || 0) -
          (a.updatedAt || a.assignedOn || a.createdAt || 0)
      );
      callback(formatted);
    };

    const unsubs = [];

    unsubs.push(
      repo.subscribeToUpdates(
        this.collectionName,
        [
          { field: "companyId", value: companyId },
          { field: "assignedTo", value: uid },
        ],
        (data) => { buckets.byAssignedTo = data; emit(); }
      )
    );

    unsubs.push(
      repo.subscribeToUpdates(
        this.collectionName,
        [
          { field: "companyId", value: companyId },
          { field: "assignedToUid", value: uid },
        ],
        (data) => { buckets.byAssignedToUid = data; emit(); }
      )
    );

    if (email) {
      unsubs.push(
        repo.subscribeToUpdates(
          this.collectionName,
          [
            { field: "companyId", value: companyId },
            { field: "assignedToName", value: email },
          ],
          (data) => { buckets.byEmail = data; emit(); }
        )
      );
    }

    return () => {
      unsubs.forEach((u) => {
        try {
          Promise.resolve(u).then((fn) => typeof fn === "function" && fn());
        } catch {}
      });
    };
  }

  /**
   * Normalize a raw asset into a UI-friendly shape.
   */
  formatAsset(asset, { userEmail, userCompany } = {}) {
    const hasQRCode = !!(
      asset.qrCodeGenerated === true ||
      asset.qrToken ||
      (asset.qrCodeValue && asset.qrCodeValue.trim() !== "") ||
      asset.qrCode ||
      asset.qrString
    );

    let qrCodeValue = asset.qrCodeValue || asset.qrCode || asset.qrString || "";
    if (hasQRCode && (!qrCodeValue || qrCodeValue.trim() === "")) {
      qrCodeValue = `ASSET:${asset.assetId}|ID:${asset.id}|TOKEN:${asset.qrToken || ""}`;
    }

    const toDate = (v) => {
      if (!v) return null;
      if (typeof v?.toDate === "function") return v.toDate();
      if (v instanceof Date) return v;
      const d = new Date(v);
      return isNaN(d.getTime()) ? null : d;
    };

    return {
      id: asset.id,
      assetId: asset.assetId || "N/A",
      name: asset.assetName || "Unnamed Asset",

      category: asset.categoryName || asset.category || "Uncategorized",
      subCategory: asset.subCategoryName || asset.subCategory || "",
      categoryCode: asset.categoryCode || "",

      brand: asset.brandName || asset.brand || "Unknown Brand",
      model: asset.model || "Not Specified",

      description:
        asset.assetDescription || asset.description || "No description available",

      // Expanded fallbacks to catch locationCode or locationId if name is missing
      location:
        asset.currentLocationName || 
        asset.locationName || 
        asset.location || 
        asset.locationCode || 
        "Unknown Location",
        
      locationCity: asset.locationCity || "",
      locationState: asset.locationState || "",
      locationZipCode: asset.locationZipCode || "",
      locationAddress:
        asset.locationAddress ||
        `${asset.locationName || ""}, ${asset.locationCity || ""}, ${asset.locationState || ""}`
          .trim()
          .replace(/^,\s*|,\s*$/g, "") ||
        asset.locationCode ||
        "Address not available",
      locationCode: asset.locationCode || "",
      department:
        asset.assignedDepartmentName ||
        asset.departmentName ||
        "Unknown Department",

      status: asset.status || "Unknown",
      assignedOn: toDate(asset.assignedOn) || toDate(asset.createdAt),
      assignedBy: asset.createdByEmail || asset.assignedBy || "System",
      assignedToName: asset.assignedToName || userEmail || "",

      purchaseValue: asset.purchaseValue || asset.cost || asset.price || 0,
      purchaseDate: toDate(asset.purchaseDate),
      invoiceNumber: asset.invoiceNumber || "N/A",
      invoiceFileUrl: asset.invoiceFileUrl || "",

      serialNumber: asset.serialNumber || "N/A",
      specifications: asset.specifications || {},

      qrCodeValue,
      qrCodeUrl: asset.qrCodeUrl || null,
      qrToken: asset.qrToken || null,
      qrCodeGenerated: asset.qrCodeGenerated || false,
      qrCodeGeneratedAt: toDate(asset.qrCodeGeneratedAt),
      hasQRCode,

      vendorId: asset.vendorId || "",
      vendorName: asset.vendorName || "Unknown Vendor",

      warrantyExpiry: toDate(asset.warrantyExpiry),
      lastMaintenance: toDate(asset.lastMaintenance),
      nextMaintenance: toDate(asset.nextMaintenance),
      condition: asset.condition || "Good",

      createdAt: toDate(asset.createdAt),
      updatedAt: toDate(asset.updatedAt),
      createdBy: asset.createdByEmail || asset.createdBy || "System",
      updatedBy: asset.updatedBy || "System",

      company: asset.company || userCompany || "Unknown Company",
      companyId: asset.companyId || "",

      alertStatus: asset.alertStatus || null,
      lastAlertSent: toDate(asset.lastAlertSent),

      depreciationSchedule: asset.depreciationSchedule || "",

      originalData: asset,
    };
  }

  /**
   * Main entry — resolves identity, fetches locations to hydrate, formats & sorts.
   */
  async getAssignedAssets(currentUser = {}) {
    const identity = this._resolveIdentity(currentUser);
    let uid = identity.uid;
    let userProfile = null;

    if (uid) {
      try {
        userProfile = await this.getUserProfile(uid);
      } catch (e) {
        console.warn("[AssignedAssetsService] getUserProfile failed:", e.message);
      }
    }

    if (!userProfile && identity.email) {
      try {
        const byEmail = await repo.fetchData("users", [
          { field: "email", value: identity.email },
        ]);
        userProfile = byEmail?.[0] || null;
        uid = userProfile?.uid || userProfile?.id || uid;
      } catch (e) {
        console.warn("[AssignedAssetsService] email lookup failed:", e.message);
      }
    }

    if (!uid && !userProfile) {
      console.warn("[AssignedAssetsService] Could not resolve uid/email; returning empty assets.");
      return { userProfile: null, assets: [] };
    }

    const companyId = userProfile?.companyId || identity.companyId || null;
    if (!companyId) {
      console.warn("[AssignedAssetsService] No companyId available; returning empty assets.");
      return { userProfile, assets: [] };
    }

    // ── FETCH ASSETS AND LOCATIONS IN PARALLEL ──
    const [rawAssets, rawLocations] = await Promise.all([
      this.fetchRawAssignedAssets({
        companyId,
        uid: uid || userProfile?.uid || userProfile?.id,
        email: identity.email || userProfile?.email,
      }),
      // Fetch master locations to map IDs to Names
      repo.fetchData(this.locationsCollection, [
        { field: "companyId", value: companyId }
      ]).catch(() => []) // Fallback to empty if location fetch fails
    ]);

    // Create a Map for quick lookup
    const locationMap = new Map();
    rawLocations.forEach((loc) => {
      const locName = loc.locationName || loc.name;
      if (loc.id) locationMap.set(loc.id, locName);
      if (loc.locationId) locationMap.set(loc.locationId, locName);
      if (loc.locationCode) locationMap.set(loc.locationCode, locName);
    });

    // Hydrate assets with actual location names
    const hydratedAssets = rawAssets.map((asset) => {
      let resolvedLocationName = asset.currentLocationName || asset.locationName;

      if (!resolvedLocationName) {
        const locKey = asset.locationId || asset.locationCode || asset.currentLocationId;
        if (locKey && locationMap.has(locKey)) {
          resolvedLocationName = locationMap.get(locKey);
        }
      }

      return {
        ...asset,
        currentLocationName: resolvedLocationName || asset.currentLocationName,
      };
    });

    const formatted = hydratedAssets.map((a) =>
      this.formatAsset(a, {
        userEmail: identity.email || userProfile?.email,
        userCompany: userProfile?.company,
      })
    );

    formatted.sort(
      (a, b) =>
        (b.updatedAt || b.assignedOn || b.createdAt || 0) -
        (a.updatedAt || a.assignedOn || a.createdAt || 0)
    );

    return { userProfile, assets: formatted };
  }

  /**
   * Compute summary stats.
   */
  computeStats(assets) {
    const now = Date.now();
    const in30Days = now + 30 * 24 * 60 * 60 * 1000;

    return {
      total: assets.length,
      active: assets.filter((a) =>
        ["assigned", "active", "in use"].includes(a.status?.toLowerCase())
      ).length,
      withQR: assets.filter((a) => a.hasQRCode).length,
      totalValue: assets.reduce((sum, a) => sum + (a.purchaseValue || 0), 0),
      expiredWarranty: assets.filter(
        (a) => a.warrantyExpiry && new Date(a.warrantyExpiry).getTime() < now
      ).length,
      expiringSoon: assets.filter((a) => {
        if (!a.warrantyExpiry) return false;
        const t = new Date(a.warrantyExpiry).getTime();
        return t > now && t < in30Days;
      }).length,
    };
  }

  /* ─────────────────────────────────────────────────────────────
   *  CRUD (preserved from original)
   * ───────────────────────────────────────────────────────────── */

  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.assetId || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      assetId: data.assetId || targetId,
      companyId: data.companyId || user?.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Asset ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  async fetchById(id) {
    if (!id) {
      console.error("[AssignedAssetsService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[AssignedAssetsService] Failed to fetch asset ${id}:`, error);
      return null;
    }
  }

  async fetchAssets({ companyId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId) companyId = user?.companyId;

    const filters = [];
    if (companyId) filters.push({ field: "companyId", value: String(companyId) });
    if (status) filters.push({ field: "status", value: status });

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[AssignedAssetsService] Failed to fetch assets:", error);
      return [];
    }
  }

  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Asset ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const nowIso = new Date().toISOString();

    finalData.metadata = {
      ...(finalData.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || finalData.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, finalData);
  }

  async delete(id) {
    if (!id) throw new Error("Asset ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[AssignedAssetsService] Failed to delete asset ${id}:`, error);
      throw error;
    }
  }
}

export const assignedAssetsService = new AssignedAssetsService();
export default assignedAssetsService;